/**
 * Auto-populates Report measurement values from two sources:
 *
 *  1. Spectral Doppler analysis results (after a spectral annotation lands)
 *  2. Hydrated annotation updates (viewport resize / "adopt AI")
 *
 * Simpson volumes have no fold of their own: a drawn volume is written by the
 * capture path and a reshaped one through the hydrated update, so a Simpson
 * result in the session (e.g. seeded by hydration) never rewrites a row.
 *
 * INVARIANT: The Spectral branch compares `currentReport` reference-equality
 * against `reportData` to skip when the editor is still the literal server
 * payload — the caller MUST pass `reportData` from the same hook tick as
 * `currentReport` (i.e. both from `useReportEditorState`). If they diverge,
 * stale Spectral results re-dirty a freshly refetched report.
 */

import { useEffect, type Dispatch, type SetStateAction } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAllSpectralDopplerResults,
  selectHydratedMeasurementUpdate,
  clearHydratedMeasurementUpdate,
} from '@/state/slices/measurementSession';
import {
  getSpectralDopplerFieldMapping,
  extractSpectralValueForField,
} from '@/utils/measurementToolMapping';
import { Annotation, Measurement, Report as MReport } from '@/models/report';
import { Study } from '@/models/study';
import type { MeasurementGroup } from '@/models/template';
import { runCascade } from '../utils/runCascade';

interface UseMeasurementAutoPopulateArgs {
  reportData: MReport | undefined;
  currentReport: MReport | null;
  // Full setStateAction support: the Spectral fold writes with the
  // functional form so they compose with a same-commit capture write instead
  // of clobbering it off a stale closure (AP-764 — dropped volume annotations).
  setCurrentReport: Dispatch<SetStateAction<MReport | null>>;
  /** Worksheet groups — the AP-731 engine's scope and formula source (R3). */
  measurementGroups?: MeasurementGroup[];
  study: Study;
}

/** Sub-pixel jitter is not an edit — the viewport coordinate round-trip shifts
 *  the last decimals on its own. */
const POINT_EPSILON = 1e-3;

/** Same idea for derived values: spectral tools recompute stats on RENDER
 *  (world→pixel round-trip + bbox clamping), so a re-derived value drifts in
 *  the trailing decimals without any user edit. A real reshape moves the value
 *  by orders of magnitude more than 0.1%. */
const VALUE_EPSILON_REL = 1e-3;

function sameValue(a: number, b: number): boolean {
  const scale = Math.max(Math.abs(a), Math.abs(b));
  if (scale === 0) return true;
  return Math.abs(a - b) <= scale * VALUE_EPSILON_REL;
}

function samePoints(
  stored: Annotation['points'] | undefined,
  hydrated: readonly (readonly number[])[] | undefined
): boolean {
  if (!stored || !hydrated || stored.length !== hydrated.length) return false;
  return stored.every((point, i) =>
    point.every(
      (coord, axis) =>
        Math.abs(coord - (hydrated[i]?.[axis] ?? NaN)) <= POINT_EPSILON
    )
  );
}

/** True when the fold would rewrite the row with what it already holds. Covers
 *  what `fingerprintMeasurement` compares: value, unit and annotation. */
function isUnchangedByHydration(
  existing: Measurement,
  annotationForUpdate: Annotation,
  update: { value: number; unit?: string }
): boolean {
  const stored = existing.annotation;
  if (!stored) return false;
  return (
    existing.value !== null &&
    sameValue(existing.value, update.value) &&
    existing.unit === (update.unit || existing.unit) &&
    stored.type === annotationForUpdate.type &&
    stored.dicomUuid === annotationForUpdate.dicomUuid &&
    stored.frameNumber === annotationForUpdate.frameNumber &&
    samePoints(stored.points, annotationForUpdate.points)
  );
}

export function useMeasurementAutoPopulate({
  reportData,
  currentReport,
  setCurrentReport,
  study,
  measurementGroups,
}: UseMeasurementAutoPopulateArgs): void {
  const dispatch = useDispatch();
  const spectralDopplerResults = useSelector(selectAllSpectralDopplerResults);
  const hydratedUpdate = useSelector(selectHydratedMeasurementUpdate);

  // Spectral Doppler — primary binding by reportMeasurementClassUuid.
  useEffect(() => {
    if (!currentReport || spectralDopplerResults.length === 0) return;
    if (currentReport === reportData) return;

    const resultsByClass = new Map(
      spectralDopplerResults
        .filter((r) => r.reportMeasurementClassUuid)
        .map((r) => [r.reportMeasurementClassUuid as string, r])
    );

    // Functional update: composes with a same-commit capture write instead of
    // clobbering its annotation off a stale closure.
    setCurrentReport((prev) => {
      if (!prev) return prev;
      const touched: string[] = [];
      const updatedMeasurements = (prev.measurements || []).map((mv) => {
        if (!mv.canonicalMeasurement) return mv;
        const bound = resultsByClass.get(mv.canonicalMeasurement);
        if (!bound) return mv;
        const mapping = getSpectralDopplerFieldMapping(
          bound.measurementLabel ?? ''
        );
        if (!mapping) return mv;
        const derived = extractSpectralValueForField(mapping, bound.values);
        if (derived == null) return mv;
        // Epsilon, not equality: spectral tools re-derive stats on RENDER, and
        // capture re-syncs the Redux result with the jittered values — a
        // worksheet click (select → re-render → StatsUpdated) must not fold a
        // recompute-drifted copy of the value the row already holds.
        if (mv.value !== null && sameValue(derived, mv.value)) return mv;
        touched.push(mv.canonicalMeasurement);
        return {
          ...mv,
          value: derived,
          updatedAt: new Date().toISOString(),
        };
      });
      if (!touched.length) return prev;
      return {
        ...prev,
        // R5 — a traced spectral value is a reader edit of that component.
        measurements: runCascade({
          report: prev,
          updated: updatedMeasurements,
          target: touched,
          action: 'edit',
          measurementGroups,
          patientSex: study.patientSex,
        }),
      };
    });
  }, [
    spectralDopplerResults,
    currentReport,
    reportData,
    setCurrentReport,
    measurementGroups,
    study.patientSex,
  ]);

  // Hydrated annotation update — resize / adopt-AI from viewport.
  useEffect(() => {
    if (!hydratedUpdate || !currentReport) return;

    const existing = currentReport.measurements.find(
      (m) => m.canonicalMeasurement === hydratedUpdate.canonicalMeasurement
    );
    const aiSource = currentReport.aiMeasurements?.find(
      (ai) =>
        !ai.isDeleted &&
        ai.canonicalMeasurement === hydratedUpdate.canonicalMeasurement
    );

    const annotationForUpdate = existing?.annotation
      ? {
          ...existing.annotation,
          points: hydratedUpdate.points,
          // A Simpson contour can land on a row whose annotation was LINE —
          // keeping LINE with a polyline fails the backend's 2-point rule.
          type: hydratedUpdate.type ?? existing.annotation.type,
          dicomUuid: hydratedUpdate.dicomUuid ?? existing.annotation.dicomUuid,
          frameNumber:
            hydratedUpdate.frameNumber ?? existing.annotation.frameNumber,
        }
      : {
          // Simpson contours pass type: 'FREEHAND'; hydrated lines omit it and
          // keep the LINE default.
          type: hydratedUpdate.type ?? ('LINE' as const),
          dicomUuid:
            hydratedUpdate.dicomUuid ??
            aiSource?.annotation?.dicom ??
            study.uuid,
          frameNumber:
            hydratedUpdate.frameNumber ??
            aiSource?.annotation?.frameNumber ??
            1,
          points: hydratedUpdate.points,
        };

    // Hydration re-emits ANNOTATION_MODIFIED for lines it just drew (AP-683),
    // and spectral tools re-derive stats on RENDER (selecting a VTI row was
    // enough to fire StatsUpdated with a jittered value) — neither is an edit.
    // The skip applies even when a live AI source exists: an unchanged fold
    // must never proceed just to retire the AI measurement — a recompute is
    // not an adoption. A real adopt/reshape always changes value or points and
    // falls through.
    if (
      existing &&
      isUnchangedByHydration(existing, annotationForUpdate, hydratedUpdate)
    ) {
      dispatch(clearHydratedMeasurementUpdate());
      return;
    }

    let nextMeasurements: Measurement[];
    if (existing) {
      nextMeasurements = currentReport.measurements.map((m) =>
        m.canonicalMeasurement === hydratedUpdate.canonicalMeasurement
          ? {
              ...m,
              value: hydratedUpdate.value,
              unit: hydratedUpdate.unit || m.unit,
              updatedAt: new Date().toISOString(),
              annotation: annotationForUpdate,
            }
          : m
      );
    } else {
      const newMeasurement: Measurement = {
        canonicalMeasurement: hydratedUpdate.canonicalMeasurement,
        value: hydratedUpdate.value,
        unit: hydratedUpdate.unit,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        annotation: annotationForUpdate,
      };
      nextMeasurements = [...currentReport.measurements, newMeasurement];
    }

    // Adopt-AI: the reader has taken ownership of what was an AI measurement,
    // so retire the AI source. Without this it lingers (never marked deleted),
    // re-hydrates as a second competing line on reload, and lets the worksheet
    // value and viewport geometry resolve to different rows for the same
    // canonical. Mirrors the soft-delete already done on the deletion path
    // (Report/hooks/useMeasurementCapture.ts). Only touch aiMeasurements when
    // an AI source actually backed this update (a plain resize has none).
    const nextAiMeasurements =
      aiSource && !aiSource.isDeleted
        ? (currentReport.aiMeasurements ?? []).map((ai) =>
            ai.canonicalMeasurement === hydratedUpdate.canonicalMeasurement
              ? { ...ai, isDeleted: true }
              : ai
          )
        : currentReport.aiMeasurements;

    setCurrentReport({
      ...currentReport,
      // R5/R8 — a reshape or an adopt-AI is a reader edit of that component.
      measurements: runCascade({
        report: currentReport,
        updated: nextMeasurements,
        target: hydratedUpdate.canonicalMeasurement,
        action: 'edit',
        measurementGroups,
        patientSex: study.patientSex,
      }),
      aiMeasurements: nextAiMeasurements,
    });
    dispatch(clearHydratedMeasurementUpdate());
  }, [
    hydratedUpdate,
    currentReport,
    dispatch,
    study.uuid,
    study.patientSex,
    setCurrentReport,
    measurementGroups,
  ]);
}
